// ─── AccidentsPanel — Módulo de Acidentes (variante fuel / Move Express) ──
//
// Vive dentro do ecrã da Frota (tab "Acidentes"). Só o operador regista.
// Cada acidente liga-se a uma OT de combustível (herda veículo/matrícula/
// motorista/produto) e a um tipo de acidente configurável (categorias que
// crescem). Fotos = data-URL base64 (padrão fuel).

import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../../App';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Button, IconButton, Chip, Tooltip, TextField, MenuItem,
  Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import AddressFieldShared from '../Orders/AddressFieldShared';

// Etiqueta pesquisável de uma OT (o Autocomplete filtra por este texto).
const otOptionLabel = (o) => o
  ? [o.customRef || o.orderNumber, o.vehiclePlate, o.driverName, o.deliveryName].filter(Boolean).join(' · ')
  : '';

const SEVERITY = [
  { key: 'minor', pt: 'Ligeiro', en: 'Minor', color: '#16a34a', bg: '#dcfce7' },
  { key: 'moderate', pt: 'Moderado', en: 'Moderate', color: '#b45309', bg: '#fef3c7' },
  { key: 'severe', pt: 'Grave', en: 'Severe', color: '#b91c1c', bg: '#fee2e2' },
];
const STATUS = [
  { key: 'open', pt: 'Aberto', en: 'Open', color: '#b45309', bg: '#fef3c7' },
  { key: 'review', pt: 'Em análise', en: 'Under review', color: '#1d4ed8', bg: '#dbeafe' },
  { key: 'closed', pt: 'Fechado', en: 'Closed', color: '#475569', bg: '#f1f5f9' },
];
const metaOf = (arr, key) => arr.find(x => x.key === key) || null;

// Velocidade do CAN bus a partir dos atributos da posição EcooGPS.
// Teltonika AVL ID 81 = "Vehicle Speed" (CAN/FMS). Palpite a confirmar com
// dados ao vivo; se o número do IO for outro, muda-se só a chave aqui.
const CAN_SPEED_IO = 'io81';
const canSpeedFrom = (attrs) => {
  const raw = attrs && attrs[CAN_SPEED_IO];
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n < 300 ? Math.round(n) : null;
};

const toLocalInput = (d) => {
  const dt = d ? new Date(d) : new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const emptyForm = () => ({
  id: null, orderId: '', vehicleId: null, vehiclePlate: '', driverName: '', fuelProduct: '',
  accidentTypeId: '', location: '', lat: null, lng: null,
  occurredAt: toLocalInput(), description: '', photoUrl: null,
  speedKmh: '', speedSource: null, otherVehicleInvolved: false, otherVehicleInfo: '',
  bodilyInjuries: false, bodilyInjuriesInfo: '', thirdPartyDamage: false, thirdPartyDamageInfo: '',
  fuelLostLitres: '', severity: '', status: 'open', policeReported: false, policeReportRef: '', conditions: '',
});

export default function AccidentsPanel({ lang = 'pt' }) {
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const { token } = useContext(AuthContext);
  const vehicles = useSelector(selectVehicles);

  const [accidents, setAccidents] = useState([]);
  const [types, setTypes] = useState([]);
  const [fuelOrders, setFuelOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState(null);          // objeto = dialog aberto
  const [detail, setDetail] = useState(null);      // acidente em visualização
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newType, setNewType] = useState('');      // input do novo tipo
  const [confirmDel, setConfirmDel] = useState(null);

  const H = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const qs = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [aR, tR] = await Promise.all([
        fetch(`/planner-api/accidents${qs}`, { headers: H }),
        fetch('/planner-api/accidents/types', { headers: H }),
      ]);
      if (aR.ok) setAccidents(await aR.json());
      if (tR.ok) setTypes(await tR.json());
    } catch (_) { /* silencioso */ } finally { setLoading(false); }
  }, [token, statusFilter, H]);

  useEffect(() => { load(); }, [load]);

  // OTs de combustível — carregam-se uma vez (para o seletor).
  useEffect(() => {
    if (!token) return;
    let cancel = false;
    (async () => {
      try {
        const r = await fetch('/planner-api/accidents/fuel-orders', { headers: H });
        if (r.ok && !cancel) setFuelOrders(await r.json());
      } catch (_) { /* silencioso */ }
    })();
    return () => { cancel = true; };
  }, [token, H]);

  // Ao escolher a OT, herda veículo/matrícula/motorista/produto + última
  // velocidade conhecida do veículo (do estado em tempo real da Frota).
  const onPickOrder = (orderId) => {
    const ot = fuelOrders.find(o => String(o.id) === String(orderId));
    setForm(f => {
      const next = { ...f, orderId };
      if (ot) {
        next.vehicleId = ot.vehicleId || null;
        next.vehiclePlate = ot.vehiclePlate || '';
        next.driverName = ot.driverName || '';
        next.fuelProduct = ot.fuelProduct || '';
        const veh = vehicles.find(v => String(v.id) === String(ot.vehicleId));
        // Preferir a velocidade do CAN bus (io81); fallback ao GPS.
        const can = canSpeedFrom(veh?.position?.attributes);
        const gps = veh?.position?.speed != null ? Math.round(Number(veh.position.speed)) : null;
        const chosen = can != null ? can : gps;
        if (chosen != null && (f.speedKmh === '' || f.speedKmh == null)) {
          next.speedKmh = chosen;
          next.speedSource = can != null ? 'CAN' : 'GPS';
        }
      }
      return next;
    });
  };

  const openNew = () => { setError(null); setForm(emptyForm()); };
  const openEdit = async (row) => {
    setError(null);
    try {
      const r = await fetch(`/planner-api/accidents/${row.id}`, { headers: H });
      const a = r.ok ? await r.json() : row;
      setForm({
        id: a.id, orderId: a.orderId || '', vehicleId: a.vehicleId, vehiclePlate: a.vehiclePlate || '',
        driverName: a.driverName || '', fuelProduct: a.fuelProduct || '', accidentTypeId: a.accidentTypeId || '',
        location: a.location || '', lat: a.lat, lng: a.lng, occurredAt: toLocalInput(a.occurredAt),
        description: a.description || '', photoUrl: a.photoUrl || null,
        speedKmh: a.speedKmh != null ? a.speedKmh : '', otherVehicleInvolved: !!a.otherVehicleInvolved,
        otherVehicleInfo: a.otherVehicleInfo || '', bodilyInjuries: !!a.bodilyInjuries, bodilyInjuriesInfo: a.bodilyInjuriesInfo || '',
        thirdPartyDamage: !!a.thirdPartyDamage, thirdPartyDamageInfo: a.thirdPartyDamageInfo || '',
        fuelLostLitres: a.fuelLostLitres != null ? a.fuelLostLitres : '', severity: a.severity || '',
        status: a.status || 'open', policeReported: !!a.policeReported, policeReportRef: a.policeReportRef || '',
        conditions: a.conditions || '',
      });
    } catch (_) { setForm({ ...emptyForm(), ...row, occurredAt: toLocalInput(row.occurredAt) }); }
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4_000_000) { setError(T('Foto demasiado grande (máx. 4 MB).', 'Photo too large (max 4 MB).')); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(p => ({ ...p, photoUrl: reader.result }));
    reader.readAsDataURL(f);
  };

  const addType = async () => {
    const name = newType.trim();
    if (!name) return;
    try {
      const r = await fetch('/planner-api/accidents/types', { method: 'POST', headers: H, body: JSON.stringify({ name }) });
      if (r.ok) {
        const created = await r.json();
        setTypes(prev => {
          const exists = prev.some(t => t.id === created.id);
          return exists ? prev : [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }));
        });
        setForm(f => ({ ...f, accidentTypeId: created.id }));
        setNewType('');
      }
    } catch (_) { /* silencioso */ }
  };

  const save = async () => {
    if (!form.orderId) { setError(T('Escolhe a OT de combustível.', 'Pick the fuel order.')); return; }
    setSaving(true); setError(null);
    try {
      const body = {
        ...form,
        occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : new Date().toISOString(),
        speedKmh: form.speedKmh === '' ? null : Number(form.speedKmh),
        fuelLostLitres: form.fuelLostLitres === '' ? null : Number(form.fuelLostLitres),
        accidentTypeId: form.accidentTypeId || null,
      };
      const url = form.id ? `/planner-api/accidents/${form.id}` : '/planner-api/accidents';
      const method = form.id ? 'PATCH' : 'POST';
      const r = await fetch(url, { method, headers: H, body: JSON.stringify(body) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || 'erro'); }
      setForm(null);
      load();
    } catch (e) { setError((e?.message) || T('Não consegui gravar.', 'Could not save.')); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      const r = await fetch(`/planner-api/accidents/${confirmDel.id}`, { method: 'DELETE', headers: H });
      if (r.ok) { setConfirmDel(null); setDetail(null); load(); }
    } catch (_) { /* silencioso */ }
  };

  const fmtDT = (d) => d ? new Date(d).toLocaleString(lang === 'pt' ? 'pt-PT' : 'en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
  const otLabel = (row) => row.orderCustomRef || row.orderNumber || (row.orderId ? `OT #${row.orderId}` : '—');

  const Badge = ({ meta }) => meta ? (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 800, color: meta.color, background: meta.bg }}>
      {lang === 'pt' ? meta.pt : meta.en}
    </span>
  ) : <span style={{ color: '#cbd5e1' }}>—</span>;

  return (
    <Box style={{ padding: '4px 2px' }}>
      {/* Cabeçalho */}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <ReportProblemIcon style={{ color: '#dc2626' }} />
        <Typography style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
          {T('Acidentes', 'Accidents')}
        </Typography>
        <Chip size="small" label={accidents.length} style={{ height: 22, fontWeight: 700, background: '#fee2e2', color: '#b91c1c' }} />
        <Box style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <TextField select size="small" variant="outlined" value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)} style={{ minWidth: 150 }}
            label={T('Estado', 'Status')}>
            <MenuItem value="all">{T('Todos', 'All')}</MenuItem>
            {STATUS.map(s => <MenuItem key={s.key} value={s.key}>{lang === 'pt' ? s.pt : s.en}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
            style={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: '#fff', borderRadius: 8 }}>
            {T('Registar acidente', 'Report accident')}
          </Button>
        </Box>
      </Box>

      {/* Tabela */}
      {loading ? (
        <Box style={{ padding: 40, textAlign: 'center' }}><CircularProgress size={26} /></Box>
      ) : accidents.length === 0 ? (
        <Box style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
          {T('Sem acidentes registados.', 'No accidents recorded.')}
        </Box>
      ) : (
        <TableContainer component={Paper} style={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          <Table size="small">
            <TableHead>
              <TableRow style={{ background: '#f8fafc' }}>
                {[T('Data/hora', 'Date/time'), T('Veículo', 'Vehicle'), 'OT', T('Tipo', 'Type'), T('Local', 'Location'), T('Gravidade', 'Severity'), T('Estado', 'Status'), ''].map((h, i) => (
                  <TableCell key={i} style={{ fontWeight: 800, fontSize: '0.68rem', color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {accidents.map(row => (
                <TableRow key={row.id} hover style={{ cursor: 'pointer' }} onClick={() => setDetail(row)}>
                  <TableCell style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{fmtDT(row.occurredAt)}</TableCell>
                  <TableCell style={{ fontSize: '0.78rem', fontWeight: 700 }}>{row.vehiclePlate || '—'}</TableCell>
                  <TableCell style={{ fontSize: '0.76rem', color: '#1e40af' }}>{otLabel(row)}</TableCell>
                  <TableCell style={{ fontSize: '0.76rem' }}>
                    {row.accidentTypeName
                      ? <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, background: row.accidentTypeColor || '#eef2ff', color: '#3730a3' }}>{row.accidentTypeName}</span>
                      : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </TableCell>
                  <TableCell style={{ fontSize: '0.75rem', color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.location || '—'}</TableCell>
                  <TableCell><Badge meta={metaOf(SEVERITY, row.severity)} /></TableCell>
                  <TableCell><Badge meta={metaOf(STATUS, row.status)} /></TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={T('Ver', 'View')}><IconButton size="small" onClick={() => setDetail(row)}><VisibilityIcon style={{ fontSize: 17 }} /></IconButton></Tooltip>
                    <Tooltip title={T('Editar', 'Edit')}><IconButton size="small" onClick={() => openEdit(row)}><EditIcon style={{ fontSize: 17, color: '#2563eb' }} /></IconButton></Tooltip>
                    <Tooltip title={T('Apagar', 'Delete')}><IconButton size="small" onClick={() => setConfirmDel(row)}><DeleteIcon style={{ fontSize: 17, color: '#dc2626' }} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Dialog de registo / edição ── */}
      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 14 } }}>
        <DialogTitle style={{ fontSize: '0.95rem', fontWeight: 800, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ReportProblemIcon style={{ fontSize: 20 }} />
          {form?.id ? T('Editar acidente', 'Edit accident') : T('Registar acidente', 'Report accident')}
        </DialogTitle>
        <DialogContent dividers>
          {form && (
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* OT de combustível — combo pesquisável (matrícula, cliente, nº) */}
              <Autocomplete
                size="small"
                options={fuelOrders}
                getOptionLabel={otOptionLabel}
                getOptionSelected={(opt, val) => opt.id === val.id}
                value={fuelOrders.find(o => String(o.id) === String(form.orderId)) || null}
                onChange={(e, val) => onPickOrder(val ? val.id : '')}
                noOptionsText={T('Sem resultados', 'No matches')}
                renderOption={(o) => (
                  <Box style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.82rem' }}>{o.customRef || o.orderNumber}</span>
                    <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                      {[o.vehiclePlate, o.driverName, o.deliveryName].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} required variant="outlined"
                    label={T('OT de combustível', 'Fuel order')}
                    placeholder={T('Pesquisar OT, matrícula, cliente…', 'Search order, plate, customer…')} />
                )}
              />
              {/* Herdado da OT */}
              {(form.vehiclePlate || form.driverName) && (
                <Box style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: '0.72rem', color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px' }}>
                  {form.vehiclePlate && <span>🚛 <b>{form.vehiclePlate}</b></span>}
                  {form.driverName && <span>👤 {form.driverName}</span>}
                  {form.fuelProduct && <span>⛽ {form.fuelProduct}</span>}
                </Box>
              )}

              {/* Tipo de acidente + adicionar novo */}
              <Box style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <TextField select size="small" variant="outlined" style={{ flex: 1 }}
                  label={T('Tipo de acidente', 'Accident type')}
                  value={form.accidentTypeId} onChange={e => setForm(f => ({ ...f, accidentTypeId: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {types.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </TextField>
                <TextField size="small" variant="outlined" style={{ width: 150 }}
                  placeholder={T('Novo tipo…', 'New type…')} value={newType}
                  onChange={e => setNewType(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addType(); } }} />
                <Button size="small" variant="outlined" onClick={addType} disabled={!newType.trim()}
                  style={{ textTransform: 'none', minWidth: 0, padding: '6px 10px' }}>+</Button>
              </Box>

              {/* Data/hora */}
              <TextField type="datetime-local" size="small" variant="outlined" fullWidth
                label={T('Data e hora', 'Date and time')} InputLabelProps={{ shrink: true }}
                value={form.occurredAt} onChange={e => setForm(f => ({ ...f, occurredAt: e.target.value }))} />

              {/* Local + GPS */}
              <AddressFieldShared
                label={T('Local do acidente', 'Accident location')} value={form.location}
                onChange={txt => setForm(f => ({ ...f, location: txt }))}
                onSelect={geo => setForm(f => ({ ...f, location: geo.name, lat: geo.coordinates?.[1] ?? null, lng: geo.coordinates?.[0] ?? null }))}
                country="AO" helperText={T('Escreve 3+ letras para sugestões com GPS', 'Type 3+ letters for GPS suggestions')} />
              {form.lat != null && form.lng != null && (
                <Typography style={{ fontSize: '0.66rem', color: '#15803d', fontWeight: 700, marginTop: -6 }}>
                  ✓ GPS: {Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)}
                </Typography>
              )}

              {/* Velocidade + combustível perdido */}
              <Box style={{ display: 'flex', gap: 10 }}>
                <TextField type="number" size="small" variant="outlined" style={{ flex: 1 }}
                  label={T('Última velocidade (km/h)', 'Last speed (km/h)')}
                  value={form.speedKmh}
                  onChange={e => setForm(f => ({ ...f, speedKmh: e.target.value, speedSource: 'manual' }))}
                  helperText={form.speedSource === 'CAN' ? T('lido do CAN bus', 'read from CAN bus')
                    : form.speedSource === 'GPS' ? T('estimativa GPS (sem CAN)', 'GPS estimate (no CAN)')
                    : form.speedSource === 'manual' ? T('introduzido manualmente', 'entered manually') : ' '}
                  InputProps={form.speedSource === 'CAN' || form.speedSource === 'GPS' ? {
                    endAdornment: (
                      <span style={{ fontSize: '0.6rem', fontWeight: 800, padding: '2px 6px', borderRadius: 6, whiteSpace: 'nowrap',
                        color: form.speedSource === 'CAN' ? '#15803d' : '#b45309',
                        background: form.speedSource === 'CAN' ? '#dcfce7' : '#fef3c7' }}>
                        {form.speedSource}
                      </span>
                    ),
                  } : undefined} />
                <TextField type="number" size="small" variant="outlined" style={{ flex: 1 }}
                  label={T('Combustível perdido (L)', 'Fuel lost (L)')}
                  value={form.fuelLostLitres} onChange={e => setForm(f => ({ ...f, fuelLostLitres: e.target.value }))} />
              </Box>

              {/* Gravidade + estado */}
              <Box style={{ display: 'flex', gap: 10 }}>
                <TextField select size="small" variant="outlined" style={{ flex: 1 }} label={T('Gravidade', 'Severity')}
                  value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {SEVERITY.map(s => <MenuItem key={s.key} value={s.key}>{lang === 'pt' ? s.pt : s.en}</MenuItem>)}
                </TextField>
                <TextField select size="small" variant="outlined" style={{ flex: 1 }} label={T('Estado', 'Status')}
                  value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {STATUS.map(s => <MenuItem key={s.key} value={s.key}>{lang === 'pt' ? s.pt : s.en}</MenuItem>)}
                </TextField>
              </Box>

              {/* Descrição */}
              <TextField multiline minRows={2} size="small" variant="outlined" fullWidth
                label={T('Descrição', 'Description')} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />

              {/* Envolvimentos / danos */}
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 4, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 10px' }}>
                <FormControlLabel control={<Checkbox size="small" checked={form.otherVehicleInvolved} onChange={e => setForm(f => ({ ...f, otherVehicleInvolved: e.target.checked }))} />}
                  label={<span style={{ fontSize: '0.8rem' }}>{T('Envolveu outro veículo', 'Another vehicle involved')}</span>} />
                {form.otherVehicleInvolved && (
                  <TextField size="small" variant="outlined" fullWidth placeholder={T('Detalhes (matrícula, condutor…)', 'Details (plate, driver…)')}
                    value={form.otherVehicleInfo} onChange={e => setForm(f => ({ ...f, otherVehicleInfo: e.target.value }))} />
                )}
                <FormControlLabel control={<Checkbox size="small" checked={form.bodilyInjuries} onChange={e => setForm(f => ({ ...f, bodilyInjuries: e.target.checked }))} />}
                  label={<span style={{ fontSize: '0.8rem' }}>{T('Houve danos corporais', 'Bodily injuries')}</span>} />
                {form.bodilyInjuries && (
                  <TextField size="small" variant="outlined" fullWidth placeholder={T('Detalhes dos ferimentos', 'Injury details')}
                    value={form.bodilyInjuriesInfo} onChange={e => setForm(f => ({ ...f, bodilyInjuriesInfo: e.target.value }))} />
                )}
                <FormControlLabel control={<Checkbox size="small" checked={form.thirdPartyDamage} onChange={e => setForm(f => ({ ...f, thirdPartyDamage: e.target.checked }))} />}
                  label={<span style={{ fontSize: '0.8rem' }}>{T('Danos a terceiros / propriedade', 'Third-party / property damage')}</span>} />
                {form.thirdPartyDamage && (
                  <TextField size="small" variant="outlined" fullWidth placeholder={T('Detalhes dos danos', 'Damage details')}
                    value={form.thirdPartyDamageInfo} onChange={e => setForm(f => ({ ...f, thirdPartyDamageInfo: e.target.value }))} />
                )}
              </Box>

              {/* Autoridades + condições */}
              <Box style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <FormControlLabel control={<Checkbox size="small" checked={form.policeReported} onChange={e => setForm(f => ({ ...f, policeReported: e.target.checked }))} />}
                  label={<span style={{ fontSize: '0.8rem' }}>{T('Participado às autoridades', 'Reported to authorities')}</span>} />
                {form.policeReported && (
                  <TextField size="small" variant="outlined" style={{ flex: 1, minWidth: 140 }} label={T('Nº participação', 'Report Nº')}
                    value={form.policeReportRef} onChange={e => setForm(f => ({ ...f, policeReportRef: e.target.value }))} />
                )}
              </Box>
              <TextField size="small" variant="outlined" fullWidth label={T('Condições (via/tempo)', 'Conditions (road/weather)')}
                value={form.conditions} onChange={e => setForm(f => ({ ...f, conditions: e.target.value }))} />

              {/* Foto */}
              <Box>
                <input id="acc-photo" type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhoto} />
                <label htmlFor="acc-photo">
                  <Button component="span" size="small" variant="outlined" startIcon={<PhotoCameraIcon style={{ fontSize: 16 }} />}
                    style={{ textTransform: 'none' }}>
                    {form.photoUrl ? T('Substituir foto', 'Replace photo') : T('Foto do acidente', 'Accident photo')}
                  </Button>
                </label>
                {form.photoUrl && (
                  <Box style={{ marginTop: 8, position: 'relative', display: 'inline-block' }}>
                    <img src={form.photoUrl} alt="" style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, border: '1px solid #e2e8f0', display: 'block' }} />
                    <IconButton size="small" onClick={() => setForm(f => ({ ...f, photoUrl: null }))}
                      style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(15,23,42,0.6)', color: '#fff' }}>
                      <CloseIcon style={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {error && <Typography style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 700 }}>{error}</Typography>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)} style={{ textTransform: 'none', color: '#64748b' }}>{T('Cancelar', 'Cancel')}</Button>
          <Button onClick={save} disabled={saving} variant="contained"
            style={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: '#fff' }}>
            {saving ? T('A gravar…', 'Saving…') : T('Guardar', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog de detalhe ── */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 14 } }}>
        {detail && <AccidentDetail id={detail.id} token={token} lang={lang}
          onClose={() => setDetail(null)}
          onEdit={() => { const d = detail; setDetail(null); openEdit(d); }}
          onDelete={() => setConfirmDel(detail)} />}
      </Dialog>

      {/* ── Confirmar apagar ── */}
      <Dialog open={!!confirmDel} onClose={() => setConfirmDel(null)} PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ fontSize: '0.9rem', fontWeight: 800 }}>{T('Apagar acidente?', 'Delete accident?')}</DialogTitle>
        <DialogContent><Typography style={{ fontSize: '0.82rem', color: '#475569' }}>{T('Esta ação é permanente.', 'This action is permanent.')}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)} style={{ textTransform: 'none', color: '#64748b' }}>{T('Cancelar', 'Cancel')}</Button>
          <Button onClick={doDelete} variant="contained" style={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', color: '#fff' }}>{T('Apagar', 'Delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Detalhe de um acidente (busca a foto completa via GET /:id).
function AccidentDetail({ id, token, lang, onClose, onEdit, onDelete }) {
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const [a, setA] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`/planner-api/accidents/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok && !cancel) setA(await r.json());
      } catch (_) { /* silencioso */ }
    })();
    return () => { cancel = true; };
  }, [id, token]);

  const fmtDT = (d) => d ? new Date(d).toLocaleString(lang === 'pt' ? 'pt-PT' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const Row = ({ label, value }) => (value == null || value === '' || value === false) ? null : (
    <Box style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
      <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', minWidth: 150 }}>{label}</Typography>
      <Typography style={{ fontSize: '0.82rem', color: '#0f172a' }}>{value === true ? T('Sim', 'Yes') : value}</Typography>
    </Box>
  );

  if (!a) return <DialogContent><Box style={{ padding: 30, textAlign: 'center' }}><CircularProgress size={24} /></Box></DialogContent>;
  const sev = metaOf(SEVERITY, a.severity), st = metaOf(STATUS, a.status);
  return (
    <>
      <DialogTitle style={{ fontSize: '0.95rem', fontWeight: 800, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 8 }}>
        <ReportProblemIcon style={{ fontSize: 20 }} /> {T('Acidente', 'Accident')} · {a.vehiclePlate || '—'}
        <span style={{ marginLeft: 'auto' }}><IconButton size="small" onClick={onClose}><CloseIcon /></IconButton></span>
      </DialogTitle>
      <DialogContent dividers>
        <Box style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          {sev && <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, color: sev.color, background: sev.bg }}>{lang === 'pt' ? sev.pt : sev.en}</span>}
          {st && <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, color: st.color, background: st.bg }}>{lang === 'pt' ? st.pt : st.en}</span>}
          {a.accidentTypeName && <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, background: '#eef2ff', color: '#3730a3' }}>{a.accidentTypeName}</span>}
        </Box>
        <Row label={T('Data/hora', 'Date/time')} value={fmtDT(a.occurredAt)} />
        <Row label={T('OT', 'Order')} value={a.orderCustomRef || a.orderNumber} />
        <Row label={T('Veículo', 'Vehicle')} value={a.vehiclePlate} />
        <Row label={T('Motorista', 'Driver')} value={a.driverName} />
        <Row label={T('Produto', 'Product')} value={a.fuelProduct} />
        <Row label={T('Local', 'Location')} value={a.location} />
        <Row label="GPS" value={a.lat != null && a.lng != null ? `${Number(a.lat).toFixed(5)}, ${Number(a.lng).toFixed(5)}` : null} />
        <Row label={T('Última velocidade', 'Last speed')} value={a.speedKmh != null ? `${a.speedKmh} km/h` : null} />
        <Row label={T('Combustível perdido', 'Fuel lost')} value={a.fuelLostLitres != null ? `${a.fuelLostLitres} L` : null} />
        <Row label={T('Outro veículo', 'Other vehicle')} value={a.otherVehicleInvolved ? (a.otherVehicleInfo || true) : null} />
        <Row label={T('Danos corporais', 'Bodily injuries')} value={a.bodilyInjuries ? (a.bodilyInjuriesInfo || true) : null} />
        <Row label={T('Danos a terceiros', 'Third-party damage')} value={a.thirdPartyDamage ? (a.thirdPartyDamageInfo || true) : null} />
        <Row label={T('Autoridades', 'Authorities')} value={a.policeReported ? (a.policeReportRef || true) : null} />
        <Row label={T('Condições', 'Conditions')} value={a.conditions} />
        <Row label={T('Descrição', 'Description')} value={a.description} />
        {a.photoUrl && (
          <Box style={{ marginTop: 10 }}>
            <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>{T('Foto', 'Photo')}</Typography>
            <img src={a.photoUrl} alt="" style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid #e2e8f0' }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onDelete} startIcon={<DeleteIcon style={{ fontSize: 16 }} />} style={{ textTransform: 'none', color: '#dc2626', marginRight: 'auto' }}>{T('Apagar', 'Delete')}</Button>
        <Button onClick={onClose} style={{ textTransform: 'none', color: '#64748b' }}>{T('Fechar', 'Close')}</Button>
        <Button onClick={onEdit} startIcon={<EditIcon style={{ fontSize: 16 }} />} variant="contained" style={{ textTransform: 'none', fontWeight: 700, background: '#2563eb', color: '#fff' }}>{T('Editar', 'Edit')}</Button>
      </DialogActions>
    </>
  );
}
